import { type Action, createAsyncThunk, type ThunkDispatch } from '@reduxjs/toolkit';

import { fetchPlans } from '../plansSlice';
import { applyCodeToProfile, setCouponCode } from '../couponSlice';
import { type AffiliateRootState } from '..';

import { switchToSuccessStep } from '../stepsSlice';
import { completeInitialization } from '@/store/new-order/initSlice';
import { sendOneTimeCodeVerifyRequest, sendPermTokenRequest } from '@/utils/api/login';
import { getParsedParams } from '@/utils/consts/params';
import { isLoggedIn, saveTokens } from '@/utils/auth';
import { selectPlanAndProceed } from '@/store/affiliate/actions/planSelect';
import type { Plan } from '@/utils/mapProductObject';
import { fetchProfile } from '@/store/new-order/userSlice';
import { extractOAuthParams, removeOAuthParams } from '@/utils/carryThroughTracking';
import AnalyticsEventHandler from '@/services/analytics/AnalyticsEventHandler';
import { clearOTCEmail, getOTCEmail } from '@/utils/otcStorage';
import { localStorageKeys } from '@/utils/consts/localStorage';

// no clear exported type here :raised_eyebrow:
interface ThunkApi {
  getState: () => AffiliateRootState;
  dispatch: ThunkDispatch<AffiliateRootState, unknown, Action>;
}

const pickPreSelectedPlan = (plans: Plan[], preselectedSku: string) =>
  plans.find((plan) => plan.sku.startsWith(preselectedSku));

const handleOauthFlow = (
  accessToken: string,
  refreshToken: string,
  code: string | null,
  savedSelectedPlanSku: string | null,
  thunkApi: ThunkApi,
) => {
  const { dispatch, getState } = thunkApi;

  const savedOAuthParams = extractOAuthParams();

  if (savedOAuthParams) {
    removeOAuthParams();
    window.location.search = `${window.location.search}&${savedOAuthParams}`;
    window.location.reload();
  }

  saveTokens(accessToken, refreshToken);

  const localSavedPlanSku = localStorage.getItem(localStorageKeys.SELECTED_PLAN_SKU);

  if (localSavedPlanSku) {
    localStorage.removeItem(localStorageKeys.SELECTED_PLAN_SKU);

    /* 
      AFAIK, selected plan is not passed as part of OAuth callback URL params
      but just in case, callback URL param should take precedence over locally stored one
    */
    if (!savedSelectedPlanSku) {
      savedSelectedPlanSku = localSavedPlanSku;
    }
  }

  return Promise.resolve()
    .then(() => {
      if (code) {
        return dispatch(applyCodeToProfile(code));
      } else {
        console.error('Locally stored code is not applied');
      }
    })
    .then(() => Promise.all([dispatch(fetchPlans()), dispatch(fetchProfile())]))
    .then(() => {
      const postInitState = getState();

      if (!postInitState.coupon || !postInitState.plans.publicPlans) {
        throw new Error('Code or plans are missing');
      }

      AnalyticsEventHandler.trackProfile(postInitState.user.profile?.account_id, postInitState.user.profile?.email);
    })
    .then(() => {
      if (savedSelectedPlanSku) {
        const selectedPlan = pickPreSelectedPlan(getState().plans.publicPlans, savedSelectedPlanSku);

        if (selectedPlan) {
          void dispatch(selectPlanAndProceed(selectedPlan));
        }
      }
    })
    .then(() => {
      dispatch(completeInitialization());
    });
};

const handleOTCEmailFlow = (
  otcCode: string,
  savedEmail: string,
  discountCode: string | null,
  savedSelectedPlanSku: string | null,
  thunkApi: ThunkApi,
) => {
  const { dispatch, getState } = thunkApi;

  return sendOneTimeCodeVerifyRequest(savedEmail, otcCode)
    .then(
      () => (discountCode ? dispatch(applyCodeToProfile(discountCode)) : null),
      () => {
        clearOTCEmail();
      },
    )
    .then(() => Promise.all([dispatch(fetchPlans()), isLoggedIn() ? dispatch(fetchProfile()) : null]))
    .then(() => {
      const postInitState = getState();

      if (!postInitState.plans.publicPlans) {
        throw new Error('Plans are missing');
      }

      if (postInitState.user.profile) {
        AnalyticsEventHandler.trackProfile(postInitState.user.profile?.account_id, postInitState.user.profile?.email);
      }

      if (savedSelectedPlanSku) {
        const selectedPlan = pickPreSelectedPlan(getState().plans.publicPlans, savedSelectedPlanSku);

        if (selectedPlan) {
          void dispatch(selectPlanAndProceed(selectedPlan));
        }
      }

      dispatch(completeInitialization());
    });
};

export const fetchAPIData = createAsyncThunk<void, void, { state: AffiliateRootState }>(
  'fetchAffiliateAPIData',
  (arg, thunkApi) => {
    const params = getParsedParams();
    const otcEmail = getOTCEmail();

    const { dispatch, getState } = thunkApi;

    console.log('params:', JSON.stringify(params));

    if (params.ACCESS_TOKEN && params.REFRESH_TOKEN && params.APP_URL) {
      console.log('OAuth callback flow');

      return handleOauthFlow(params.ACCESS_TOKEN, params.REFRESH_TOKEN, params.PROMOCODE, params.PLAN, thunkApi);
    }

    if (params.OTC_CODE && otcEmail) {
      console.log('OTC Email callback flow');

      return handleOTCEmailFlow(params.OTC_CODE, otcEmail, params.PROMOCODE, params.PLAN, thunkApi);
    }

    console.log('New entry flow');

    if (params.PROMOCODE) {
      dispatch(setCouponCode(params.PROMOCODE));
    }

    void Promise.resolve()
      .then(() => (params.PERMANENT_TOKEN ? sendPermTokenRequest(params.PERMANENT_TOKEN) : null))
      .then(() => (isLoggedIn() ? dispatch(fetchProfile()) : null))
      .finally(() => dispatch(fetchPlans()))
      .then(() => {
        const postInitState = getState();

        if (postInitState.user.profile?.subscription?.hasActiveSubscription) {
          dispatch(switchToSuccessStep());
        }
      })
      .then(() => dispatch(completeInitialization()));
  },
);
